import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Typography, Checkbox, FormControlLabel, TextField, Button } from "@mui/material";
import { Settings } from "@mui/icons-material";
import { useParams, useLocation } from 'react-router-dom';
import { SectionPaper, SectionHeader } from "../../../common/components/styled-ui-elements";
import { CenterType } from "../../../../services/ConfigurationService/types/centerType";
import { useGetCenterQuery, useUpdateCenterMutation } from "../../../../services/ConfigurationService";

// Only the string-valued keys, so a boolean field such as allowManualScheduling
// cannot be added to the text-input array and rendered as "true".
type AppConfigTextKey = {
  [K in keyof CenterType["appConfig"]]-?: CenterType["appConfig"][K] extends string ? K : never
}[keyof CenterType["appConfig"]];

type AppConfigField = {
  label: string;
  type: "text" | "email";
  name: AppConfigTextKey;
};

// The App Config tab's three text inputs differ only by these three values.
const appConfigTextFields: AppConfigField[] = [
  { label: "Auth Notification Email", type: "email", name: "authNotificationEmail" },
  { label: "Staff Notification Group ID", type: "text", name: "staffNotificationGroupId" },
  { label: "Sage Location", type: "text", name: "sageLocation" }
];

const appConfigInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    '&:hover fieldset': {
      borderColor: '#3b82f6',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
    },
  }
};

const AppConfigTextField: React.FC<{
  field: AppConfigField;
  value: string;
  onValueChange: (name: AppConfigTextKey, value: string) => void;
}> = ({ field, value, onValueChange }) => (
  <TextField
    label={field.label}
    type={field.type}
    value={value}
    name={field.name}
    onChange={e => onValueChange(field.name, e.target.value)}
    fullWidth
    size="small"
    sx={appConfigInputSx}
  />
);

// Blank and 0 are different answers: blank means the board applies its own 30-minute
// default, 0 means the facility deliberately set no buffer. A cleared field writes
// undefined, never 0.
const TurnoverBufferField: React.FC<{
  value: number | undefined;
  onValueChange: (name: "turnoverBufferMinutes", value: number | undefined) => void;
}> = ({ value, onValueChange }) => (
  <TextField
    label="Turnover Buffer (Minutes)"
    type="number"
    value={value ?? ''}
    name="turnoverBufferMinutes"
    onChange={e => onValueChange("turnoverBufferMinutes", e.target.value === '' ? undefined : Number(e.target.value))}
    fullWidth
    size="small"
    sx={appConfigInputSx}
  />
);

export const AppConfigSection: React.FC = () => {
  const { id: centerId } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const company = queryParams.get('company') || '';

  const [localAppConfig, setLocalAppConfig] = useState<CenterType["appConfig"]>({
    allowManualScheduling: false,
    authNotificationEmail: '',
    staffNotificationGroupId: '',
    sageLocation: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  const {
    data: center,
    isLoading: isLoadingCenter
  } = useGetCenterQuery({ id: centerId || '', company });

  const [updateCenter, { isLoading: isUpdating, isSuccess: success }] = useUpdateCenterMutation();

  // Initialize local state when center data loads
  useEffect(() => {
    if (center?.appConfig) {
      setLocalAppConfig(center.appConfig);
      setHasChanges(false);
    }
  }, [center]);

  const onChange = (field: keyof CenterType["appConfig"], value: any) => {
    setLocalAppConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const onSave = async () => {
    if (!center || !hasChanges) return;

    try {
      await updateCenter({
        ...center,
        appConfig: localAppConfig
      }).unwrap();
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update center app config:', error);
    }
  };

  // Auto-save when changes are made (debounced)
  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(() => {
        onSave();
      }, 1000); // 1 second debounce

      return () => clearTimeout(timer);
    }
  }, [localAppConfig, hasChanges]);

  if (isLoadingCenter) {
    return (
      <SectionPaper>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      </SectionPaper>
    );
  }
  return (
    <SectionPaper>
      <SectionHeader 
        icon={<Settings sx={{ mr: 1, fontSize: 20 }} />} 
        title="Application Configuration" 
      />
      
      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 500 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={localAppConfig?.allowManualScheduling || false}
              name="allowManualScheduling"
              onChange={e => onChange("allowManualScheduling", e.target.checked)}
              size="small"
            />
          }
          label="Allow Manual Scheduling"
          sx={{ 
            color: '#334155', 
            fontWeight: 500,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.95rem'
            }
          }}
        />
        
        {appConfigTextFields.map(field => (
          <AppConfigTextField
            key={field.name}
            field={field}
            value={localAppConfig?.[field.name] || ''}
            onValueChange={onChange}
          />
        ))}
        <TurnoverBufferField value={localAppConfig.turnoverBufferMinutes} onValueChange={onChange} />

        {success && (
          <Typography 
            color="success.main" 
            sx={{ 
              mt: 1, 
              fontSize: '0.9rem',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            ✓ App Configuration updated successfully!
          </Typography>
        )}
        
        {isUpdating && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Saving changes...
            </Typography>
          </Box>
        )}
      </Box>
    </SectionPaper>
  );
};